import React, { useState, useEffect, useRef } from 'react';
import { Bug, X, Copy, Check, Trash2 } from 'lucide-react';

interface LogEntry {
  type: 'log' | 'error' | 'warn' | 'network';
  timestamp: Date;
  message: string;
  data?: string;
}

// Global log storage
const logs: LogEntry[] = [];
const MAX_LOGS = 100;

function addLog(entry: LogEntry) {
  logs.unshift(entry);
  if (logs.length > MAX_LOGS) {
    logs.pop();
  }
  // Dispatch custom event to notify component
  window.dispatchEvent(new CustomEvent('debug-log-added'));
}

// Intercept console methods
const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
};

let intercepted = false;

function interceptConsole() {
  if (intercepted) return;
  intercepted = true;

  console.log = (...args) => {
    originalConsole.log(...args);
    addLog({
      type: 'log',
      timestamp: new Date(),
      message: args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' '),
    });
  };

  console.error = (...args) => {
    originalConsole.error(...args);
    addLog({
      type: 'error',
      timestamp: new Date(),
      message: args.map(a => {
        if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack}`;
        if (typeof a === 'object') return JSON.stringify(a, null, 2);
        return String(a);
      }).join(' '),
    });
  };

  console.warn = (...args) => {
    originalConsole.warn(...args);
    addLog({
      type: 'warn',
      timestamp: new Date(),
      message: args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' '),
    });
  };
}

// Intercept fetch for network errors
const originalFetch = window.fetch.bind(window);

function interceptFetch() {
  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof URL ? args[0].toString() : (args[0] as Request).url;
    const method = (args[1]?.method || 'GET').toUpperCase();

    try {
      const response = await originalFetch(...args);

      // Log non-2xx responses
      if (!response.ok) {
        const clonedResponse = response.clone();
        let body = '';
        try {
          body = await clonedResponse.text();
        } catch {}

        addLog({
          type: 'network',
          timestamp: new Date(),
          message: `${method} ${url} - ${response.status} ${response.statusText}`,
          data: body,
        });
      }

      return response;
    } catch (error) {
      addLog({
        type: 'network',
        timestamp: new Date(),
        message: `${method} ${url} - FAILED`,
        data: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  };
}

// Initialize interception
interceptConsole();
interceptFetch();

export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([...logs]);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState<'all' | 'error' | 'network'>('all');

  useEffect(() => {
    const handleLogAdded = () => {
      setLogEntries([...logs]);
    };

    window.addEventListener('debug-log-added', handleLogAdded);
    return () => window.removeEventListener('debug-log-added', handleLogAdded);
  }, []);

  const filteredLogs = filter === 'all'
    ? logEntries
    : logEntries.filter(l => filter === 'error' ? (l.type === 'error' || l.type === 'network') : l.type === filter);

  const copyLogs = () => {
    const text = filteredLogs.map(l => {
      const time = l.timestamp.toISOString();
      const prefix = `[${time}] [${l.type.toUpperCase()}]`;
      return l.data ? `${prefix} ${l.message}\n${l.data}` : `${prefix} ${l.message}`;
    }).join('\n\n');

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearLogs = () => {
    logs.length = 0;
    setLogEntries([]);
  };

  const errorCount = logEntries.filter(l => l.type === 'error' || l.type === 'network').length;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 hover:text-white/50 transition-colors relative"
      >
        <Bug size={12} />
        <span>Debug</span>
        {errorCount > 0 && (
          <span className="absolute -top-1 -right-2 w-4 h-4 bg-red-500 rounded-full text-[8px] flex items-center justify-center text-white font-bold">
            {errorCount > 9 ? '9+' : errorCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
      <div className="relative bg-slate-900 rounded-t-xl w-full max-w-2xl max-h-[70vh] flex flex-col border border-white/10">
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-sm">Debug Logs</h3>
            <div className="flex gap-1">
              {(['all', 'error', 'network'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2 py-0.5 text-xs rounded ${filter === f ? 'bg-foretell-accent text-white' : 'bg-white/10 text-white/60'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearLogs}
              className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white"
              title="Clear logs"
            >
              <Trash2 size={14} />
            </button>
            <button
              onClick={copyLogs}
              className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white"
              title="Copy logs"
            >
              {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 font-mono text-xs">
          {filteredLogs.length === 0 ? (
            <div className="text-center text-white/40 py-8">No logs yet</div>
          ) : (
            filteredLogs.map((log, i) => (
              <div
                key={i}
                className={`p-2 mb-1 rounded ${
                  log.type === 'error' ? 'bg-red-500/20 text-red-300' :
                  log.type === 'warn' ? 'bg-yellow-500/20 text-yellow-300' :
                  log.type === 'network' ? 'bg-orange-500/20 text-orange-300' :
                  'bg-white/5 text-white/70'
                }`}
              >
                <div className="flex items-center gap-2 text-[10px] text-white/40 mb-1">
                  <span>{log.timestamp.toLocaleTimeString()}</span>
                  <span className="uppercase font-bold">{log.type}</span>
                </div>
                <div className="whitespace-pre-wrap break-all">{log.message}</div>
                {log.data && (
                  <div className="mt-1 pt-1 border-t border-white/10 text-white/50 whitespace-pre-wrap break-all">
                    {log.data}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
